import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { CoreModule } from 'app/core/core.module';
import { LoadingService } from 'app/core/services/loading.service';
import { loadP2PCampaignList, peerToPeerReport } from 'app/manage-p2p/features/p2p.actions';
import { selectFundraiserReport } from 'app/manage-p2p/features/p2p.selectors';
import { ModalFullfillBulkPledgesService } from 'app/manage-p2p/services/modal-fulfill-bulk-donor-pledges/modal-fulfill-bulk-donor-pledges.service';
import { PeerToPeerManageService } from 'app/manage-p2p/services/peer-to-peer-manage/peer-to-peer-manage.service';
import { ToastService } from 'app/toast/services/toast.service';
import { MdbFormsModule } from 'mdb-angular-ui-kit/forms';
import { catchError, concatMap, delay, EMPTY, filter, from, last, repeatWhen, retry, retryWhen, takeWhile, tap, timer, toArray } from 'rxjs';

@Component({
  selector: 'app-p2p-fulfill-bulk-donor-pledges',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule, 
    ReactiveFormsModule, 
    CoreModule, 
    MdbFormsModule,
  ],
  templateUrl: './p2p-fulfill-bulk-donor-pledges.component.html',
  styleUrl: './p2p-fulfill-bulk-donor-pledges.component.scss'
})
export class P2pFulfillBulkDonorPledgesComponent implements OnInit{
  destroyRef = inject(DestroyRef)

  @Input() data;

  public campaign: any = null;
  public placeOfCall: string = '';

  public eventCountErrorText: string = 'Invalid Count';
  public maxEventCount: number = 0;

  private fundraiserReport$ = this.store.pipe(
    select(selectFundraiserReport)
  );

  public donorReport: any[] = [];
  public form = new FormGroup({});
  public btnDisabled: boolean = true;

  constructor(
    private store:Store,
    private p2pFulfillBulkPledgesService: ModalFullfillBulkPledgesService,
    private peerToPeerManageService: PeerToPeerManageService,
    private toastService: ToastService,
    private loadingService: LoadingService
  ) { }

  ngOnInit(): void {
    this.campaign = this.data.campaign;
    this.placeOfCall = this.data.placeOfCall;
    this.btnDisabled = true;

    this.maxEventCount = this.campaign.campaignTypeData?.maxEventCount || 0;
    this.eventCountErrorText = `Provide milestones from 1 to ${this.maxEventCount}`

    // if (this.placeOfCall === 'campaign-card') {
      this.store.dispatch({
        type: peerToPeerReport.type,
        payload: this.campaign.id
      });
    // }

    this.fundraiserReport$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((donorReport: any[]) => {
      // Filter out donors with no valid pledges and initialize amountCharged
      const filteredDonorReport = donorReport
        .map(donor => ({
          ...donor,
          pledges: (donor.pledges || [])
            .filter(pledge => pledge.status == 0)
            .map(pledge => ({
              ...pledge,
              amountCharged: pledge.amount * (donor.eventCount || 0)
            }))
        }))
        .filter(donor => donor.pledges?.length > 0);

      this.donorReport = filteredDonorReport;

      if (this.donorReport.length > 0) {
        this.btnDisabled = false;
        this.initForm();
        this.subscribeToFormChanges();
      }
    });
  }

  subscribeToFormChanges() {
    this.form.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      const updatedReport = [...this.donorReport.map(donor => {
        const control = this.form.get(donor.id);
        
        if (!control || control.invalid) {
          return {...donor};
        }

        return {
          ...donor,
          pledges: [...donor.pledges.map(pledge => ({
            ...pledge,
            amountCharged: pledge.amount * parseInt(control.value)
          }))]
        };
      })];

      this.donorReport = updatedReport;
    });
  }

  initForm() {
    const formControls = {};
    
    this.donorReport.forEach(donor => {
      if (donor.id) {
        formControls[donor.id] = new FormControl('', [
          Validators.required,
          Validators.min(1),
          Validators.max(this.maxEventCount)
        ]);

        if (donor.eventCount > 0) {
          formControls[donor.id].setValue(donor.eventCount);
        }
      }
    });

    this.form = new FormGroup(formControls);
  }

  close() {
    this.p2pFulfillBulkPledgesService.close();
  }

  onFulfillBulkPledges() {
    this.form.markAllAsTouched();
    
    if (!this.form.valid) {
      const firstInvalidControl = Object.keys(this.form.controls)
        .find(key => this.form.controls[key].invalid);

      if (firstInvalidControl) {
        setTimeout(() => {
          const element = document.getElementById(firstInvalidControl);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      }
      return;
    }

    const updates = Object.keys(this.form.controls)
      .map(donorId => {
        const eventCount = parseInt(this.form.get(donorId).value);
        const donor = this.donorReport.find(d => d.id === donorId);
        
        if (donor && eventCount >= 0) {
          return {
            p2pId: donorId,
            eventCount: eventCount
          };
        }
        return null;
      })
      .filter(update => update !== null);

    if (updates.length > 0) {
      this.btnDisabled = true;
      this.loadingService.loading = true;

      from(updates).pipe(
        concatMap(update => this.peerToPeerManageService.fulfillAllDonors(update)),
        toArray(),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe({
        next: (results) => {
          const failedCount = updates.length - results.length;
          this.btnDisabled = false;
          this.loadingService.loading = false;

          if (failedCount > 0) {
            this.toastService.show('error', 'Failed to fulfill some pledges', {delay: 2000});
          } else {
            this.toastService.show('success', 'Fulfilled successfully!', {delay: 2000});
          }

          if (this.placeOfCall === 'campaign-details') {
            this.store.dispatch({
              type: peerToPeerReport.type,
              payload: this.campaign.id
            });
          } else {
            this.store.dispatch({
              type: loadP2PCampaignList.type
            });
          }
          this.close();
        },
        error: () => {
          this.btnDisabled = false;
          this.loadingService.loading = false;
          this.close();
        }
      });
    } else {
        this.close();
    }
  }
}
